import { Component, inject, Input, OnInit, signal } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";

import { AdaaHelper } from "../../../../core/utils";
import { Constants } from "../../../constants/constants";
import { PageMode } from "../../../constants/enums";
import { SubGovDirection, ValidatorModelType, ValueText } from "../../../models";
import { PropertiesService, ValidatorApiService } from "../../../services";
import { FormCheckboxComponent, FormDropdownComponent, FormInputComponent } from "../../form";

@Component({
  selector: "adaa-objective-enabler-modal",
  imports: [ReactiveFormsModule, TranslatePipe, FormInputComponent, FormCheckboxComponent, FormDropdownComponent],
  templateUrl: "./objective-enabler-modal.html",
  styleUrl: "./objective-enabler-modal.scss",
})
export class ObjectiveEnablerModalComponent implements OnInit {
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _translateService = inject(TranslateService);
  private readonly _toastrService = inject(ToastrService);
  private readonly _propsService = inject(PropertiesService);
  private readonly _validationApiService = inject(ValidatorApiService);
  readonly modal: NgbActiveModal = inject(NgbActiveModal);

  @Input() subDirection: SubGovDirection;
  @Input() pageMode: PageMode = PageMode.view;
  @Input() validations: ValidatorModelType;
  @Input() ownerGroups: ValueText[] = [];

  subDirectionForm: FormGroup;
  PageMode = PageMode;
  isFieldRequired = AdaaHelper.isFieldRequired.bind(AdaaHelper);
  submitted: boolean = false;
  subGovDirTypes = signal<ValueText[]>([]);

  public ngOnInit(): void {
    this._getSubGovDirTypes();
    this._prepareForm();
    this.subDirectionForm.patchValue(this.subDirection);
  }

  public save(): void {
    this.submitted = true;
    if (!this.subDirectionForm.valid) {
      this._toastrService.warning(this._translateService.instant("notification.warning.missing_info"));
      return;
    }

    const owner = this.subDirectionForm.get("owner")?.value as number;
    const ownerNameAE = this.ownerGroups.find((e) => e.value === owner)?.text ?? "";
    const ownerNameEN = this.ownerGroups.find((e) => e.value === owner)?.text ?? "";

    this.subDirection.nameAE = this.subDirectionForm.get("nameAE")?.value;
    this.subDirection.nameEN = this.subDirectionForm.get("nameEN")?.value;
    this.subDirection.dscAE = this.subDirectionForm.get("dscAE")?.value;
    this.subDirection.dscEN = this.subDirectionForm.get("dscEN")?.value;
    this.subDirection.isMain = this.subDirectionForm.get("isMain")?.value;
    this.subDirection.owner = owner;
    this.subDirection.ownerNameAE = ownerNameAE;
    this.subDirection.ownerNameEN = ownerNameEN;
    this.subDirection.type = this.subDirectionForm.get("type")?.value;

    this.modal.close(this.subDirection);
  }

  private _prepareForm() {
    const isViewMode = this.pageMode === PageMode.view;

    this.subDirectionForm = this._formBuilder.group({
      id: { value: null, disabled: isViewMode },
      refCode: { value: null, disabled: true },
      govDirectionId: { value: null, disabled: isViewMode },
      nameAE: { value: null, disabled: isViewMode },
      nameEN: { value: null, disabled: isViewMode },
      dscAE: { value: null, disabled: isViewMode },
      dscEN: { value: null, disabled: isViewMode },
      owner: { value: 0, disabled: isViewMode },
      logo: { value: null, disabled: isViewMode },
      isMain: { value: false, disabled: isViewMode },
      type: { value: null, disabled: isViewMode },
    });

    this.subDirectionForm = this._validationApiService.setValidationFromData(this.validations, this.subDirectionForm);
  }

  private _getSubGovDirTypes() {
    this._propsService.getPropById(Constants.CONSTANT_NATIONAL_STRATEGY_TYPES).subscribe({
      next: (response) => {
        if (response.inError) return;
        this.subGovDirTypes.set(
          AdaaHelper.setDropdownArray(response.responseData, "id", AdaaHelper.getFieldLanguage("name"))
        );
      },
    });
  }
}
